import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import Image from "../images/Image";
import DropdownFramer from "../framerMotion/DropdownFramer";
import useTranslation from "../CustomHooks/useTranslation";
import { SideBarContent } from "./SideBarContent";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import { setPreviousUrl } from "../../store/slices/geolocationSlice";
import { useSelector } from "react-redux";

interface DropdownItem {
  option: string;
  path: string;
  privilege: string;
  disabled?: boolean;
}

interface SidebarItemProps {
  item: {
    label: string;
    dropdown?: string[] | DropdownItem[];
    link?: string;
    privileges?: string | string[];
  };
  isActiveTab: {
    activeTab: string;
    dropdownOpen: boolean;
  };
  setIsActiveTab: React.Dispatch<
    React.SetStateAction<{
      activeTab: string;
      dropdownOpen: boolean;
    }>
  >;
  hasADDMDPrivilege: boolean;
  hasListMDPrivilege: boolean;
  dropdownOptions?: Record<string, boolean>;
  setDropdownOptions: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
}


const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  isActiveTab,
  setIsActiveTab,
  hasADDMDPrivilege,
  hasListMDPrivilege,
  dropdownOptions,
  setDropdownOptions
}) => {
  if (!item) return null;
  const { translate } = useTranslation(SideBarContent);
  const { label, dropdown, link } = item;
  const [activeDropdownItems, setActiveDropdownItems] = useState<{ [key: string]: boolean }>({});
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { privileges } = useSelector((state: RootState) => state.privileges);

  // Get the complete path with query parameters
  const currentPath = location.search;

  // Don't render if it's Master Distributor Management and both privileges are false
  if ((label === "Master Distributor Management") && !hasADDMDPrivilege && !hasListMDPrivilege) {
    return null;
  }

  const src = `${label.toLowerCase().replace(/ /g, "-").replace('/', '-')}`;
  const to = link ? link : `/${src}`;

  // Type guard for dropdown items
  const isDropdownItem = (item: string | DropdownItem): item is DropdownItem => {
    return typeof item !== "string" && "option" in item;
  };

  // Helper function to generate the link for dropdown items
  const getLink = (dropdownItem: string | DropdownItem) => {
    if (isDropdownItem(dropdownItem)) {
      return dropdownItem.path;
    }
    return `${to}/${dropdownItem.toLowerCase().replace(/ /g, "-")}`;
  };

  // Get display text for dropdown item
  const getDisplayText = (dropdownItem: string | DropdownItem) => {
    return isDropdownItem(dropdownItem) ? dropdownItem.option : dropdownItem;
  };
  // Determine if the current item is active
  const   isActive = isActiveTab.activeTab.split("/")[1] === to.split("/")[1];

  const handleTabClick = (e: React.MouseEvent) => {
    if (dropdown) {
      e.preventDefault();

      // If clicking a different dropdown, only update activeTab & dropdownOpen
      if (isActiveTab.activeTab.split("/")[1] !== to.split("/")[1]) {
        setIsActiveTab({ activeTab: to, dropdownOpen: true });
        setActiveDropdownItems({});
      } else {
        setIsActiveTab(prev => ({ ...prev, dropdownOpen: !prev.dropdownOpen }));
      }
    } else {
      setIsActiveTab({ activeTab: to, dropdownOpen: false });
      setActiveDropdownItems({});
      setDropdownOptions({})
      dispatch(setPreviousUrl(''))
    }
  };

  const handleDropdownItemClick = (dropdownItem: string | DropdownItem) => {
    const itemLink = getLink(dropdownItem);
    const newActiveItems: { [key: string]: boolean } = {};
    if (dropdown) {
      dropdown.forEach(item => {
        const itemKey = isDropdownItem(item) ? item.path : `${to}/${item.toLowerCase().replace(/ /g, "-")}`;
        newActiveItems[itemKey] = itemKey === itemLink;
      });
    }
    setActiveDropdownItems(newActiveItems);
    setIsActiveTab({ activeTab: itemLink, dropdownOpen: true });

    // Update the dropdownOptions state
    if (dropdownOptions && setDropdownOptions) {
      const displayText = getDisplayText(dropdownItem);

      // Create a new object with all options set to false
      const updatedOptions = Object.keys(dropdownOptions).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      // Set only the selected option to true
      updatedOptions[displayText] = true;

      // Update the state
      setDropdownOptions(updatedOptions);
    }

    dispatch(setPreviousUrl(''))
  };
  const checkPrivileges = (itemPrivileges: string | string[] | undefined): boolean => {
    // Return false if no privileges are specified
    if (!itemPrivileges || !privileges) {
      return false;
    }
    if (privileges === 'superadmin') {
      return true;
    }
    // Handle 'allowed' case
    if (itemPrivileges === 'allowed') {
      return true;
    }

    // Handle array of privileges
    if (Array.isArray(itemPrivileges)) {
      return itemPrivileges.some(privilege => privileges.includes(privilege));
    }
    // Handle single privilege string
    return privileges.includes(itemPrivileges);
  };
  return (
    <div className="mb-5 pr-2.5">
      <div
        className={`px-2.5 py-2 w-[259px] rounded-lg ${isActive ? "bg-p_light" : ""
          }`}
      >
        <Link
          to={to}
          className={`text-body1 flex justify-between ${isActive ? "bg-p_light text-p_primary" : "text-primary_text"
            }`}
          onClick={handleTabClick}
        >
          <div className="flex items-center">
            <Image
              src={src}
              className={`h-6 w-6 mr-[21px] ${isActive ? "image-active-filter" : ""
                }`}
            />
            {translate(label)}
          </div>
          {dropdown && (
            <DropdownFramer isDropdownVisible={isActive && isActiveTab.dropdownOpen}>
              <Image
                src="dropdown-down-arrow"
                className={`${isActive ? "image-active-filter" : ""}`}
              />
            </DropdownFramer>
          )}
        </Link>

        {dropdown && isActiveTab.dropdownOpen && isActive && (
          <div className="ml-10 mt-2">
            {dropdown
              .map((dropdownItem, index) => {
                const itemLink = getLink(dropdownItem);
                const displayText = getDisplayText(dropdownItem);

                // Use the dropdownOptions state to determine if this item should be highlighted
                const isItemActive = dropdownOptions && dropdownOptions[displayText] === true;

                const isDisabled = isDropdownItem(dropdownItem) && dropdownItem.disabled;

                return checkPrivileges(dropdownItem.privilege) && (
                  <Link
                    to={isDisabled ? "#" : itemLink}
                    key={index}
                    className={`mb-1 text-p_primary flex text-medium rounded-lg py-2.5 pr-1 w-[208px] ${isItemActive ? "bg-background_blue" : ""} ${isDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    onClick={(e) => {
                      if (isDisabled) { e.preventDefault(); return; }
                      handleDropdownItemClick(dropdownItem);
                    }}
                  >
                    <div
                      className={`w-[3px] ${isItemActive
                        ? "bg-p_primary rounded-r-[10px] border-r border-r-p_primary"
                        : ""
                        }`}
                    ></div>
                    <div className="pl-1.5">{translate(displayText)}</div>
                  </Link>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarItem;